export interface Actor {
  displayName: string;
  id: string;
  email?: string;
  inventoryId?: string;
}

export enum StepType {
  userMessageStep = 'userMessageStep',
  messageStep = 'messageStep',
  toolInvocationStep = 'toolInvocationStep',
  // Tool request/response step types - two variants exist for backward compatibility:
  // - toolInvocationRequestStep/toolInvocationResultStep: Original types used by inline activity pipeline
  // - toolRequestStep/toolResponseStep: New types used by unified pipe (audit events pipeline)
  // Both pairs have identical UI behavior but different API values
  toolInvocationRequestStep = 'toolCallRequestStep',
  toolInvocationResultStep = 'toolCallResultStep',
  toolRequestStep = 'toolInvocationRequestStep',
  toolResponseStep = 'toolInvocationResponseStep',
  ragStep = 'ragStep',
  aiMessageStep = 'aiMessageStep',
  triggerStep = 'triggerStep',
  memoryStep = 'memoryStep',
  agentHandoffRequestStep = 'agentHandoffRequestStep',
  agentHandoffResponseStep = 'agentHandoffResponseStep',
}

export enum AiService {
  m365Copilot = 'm365Copilot',
  salesforceEinstein = 'salesforceEinstein',
  agentforce = 'agentforce',
  copilotStudio = 'copilotStudio',
  chatGptEnterprise = 'chatGptEnterprise',
  azureAiFoundry = 'azureAiFoundry',
  vertexAi = 'vertexAi',
  geminiEnterpriseAgentDesigner = 'geminiEnterpriseAgentDesigner',
  powerAutomate = 'powerAutomate',
  powerApps = 'powerApps',
  powerPages = 'powerPages',
  bedrock = 'bedrock',
  serviceNow = 'serviceNow',
  microsoft365 = 'Microsoft365',
  gitHub = 'gitHub',
  slack = 'slack',
  bedrockAgentCore = 'bedrockAgentcore',
  // Device-based AI services
  claude = 'claude',
  perplexity = 'perplexity',
  cursor = 'cursor',
  windsurf = 'windsurf',
  tabnine = 'tabnine',
  amazonQ = 'amazonQ',
  dia = 'dia',
  agentKit = 'agentKit',
  claudeCode = 'claude_code',
  homegrown = 'homegrown',
  customAgent = 'customAgent',
  langchain = 'langchain',
  openaiCodex = 'openaiCodex',
}

// TODO: reuse values from AiService after they are fixed on BE (now values are inconsistent on BE for different AI Services)
export enum FindingAiService {
  m365Copilot = 'm365Copilot',
  salesforceEinstein = 'salesforceEinstein',
  agentforce = 'agentforce',
  copilotStudio = 'copilotStudio',
  chatGptEnterprise = 'chatGptEnterprise',
  azureAiFoundry = 'azureAiFoundry',
  vertexAi = 'vertexAi',
  powerAutomate = 'powerAutomate',
  powerApps = 'powerApps',
  powerPages = 'powerPages',
  bedrock = 'bedrock',
  bedrockAgentCore = 'bedrockAgentcore',
  serviceNow = 'serviceNow',
  sharepointAgents = 'sharepointAgents',
  claudeCode = 'claude_code',
  homegrown = 'homegrown',
  customAgent = 'customAgent',
  langchain = 'langchain',
  openaiCodex = 'openaiCodex',
}

// TODO: reuse values from AiService after they are fixed on BE (now values are inconsistent on BE for different AI Services)
export enum InLineControlAiService {
  gitHub = 'github',
  slack = 'slack',
  m365Copilot = 'm365copilot',
  azureAiFoundry = 'azureAiFoundry',
  chatGptEnterprise = 'chatGptEnterprise',
  claude = 'claude',
  copilotStudio = 'copilotStudio',
  agentKit = 'agentKit',
  perplexity = 'perplexity',
  cursor = 'cursor',
  unknown = 'unknown',
}

export enum ChatMode {
  bizchat = 'work',
  appchat = 'web',
  na = 'N/A',
}

export enum ActivitySource {
  auditlog = 'auditEvents',
  inline = 'inline',
}

export interface Indicator {
  id: string;
}

export enum AgentClientApplication {
  office = 'office',
  teams = 'teams',
  word = 'word',
  excel = 'excel',
  powerpoint = 'powerpoint',
  outlook = 'outlook',
  onenote = 'onenote',
  appchat = 'appchat',
  sharepoint = 'sharepoint',
  stream = 'stream',
  forms = 'forms',
  bing = 'bing',
  agentWebsite = 'agentWebsite',
  testing = 'testing',
  autonomous = 'autonomous',
  chatGpt = 'chatGpt',
  azureAiFoundry = 'azureAiFoundry',
  vertexAi = 'vertexAi',
  serviceNow = 'serviceNow',
  copilotStudio = 'copilotStudio',
  gitHubCopilotVisualStudio = 'gitHubCopilotVisualStudio',
  claudeDesktop = 'claudeDesktop',
  copilot = 'copilot',
  perplexity = 'perplexity',
  gitHubCopilotWeb = 'gitHubCopilotWeb',
  cursor = 'cursor',
  chatKit = 'chatKit',
  claudeCode = 'claudeCode',
  slack = 'slack',
  CODEX_CLI = 'CODEX_CLI',
  CODEX_WEB = 'CODEX_WEB',
  CODEX_IDE_VSCODE = 'CODEX_IDE_VSCODE',
  CODEX_GITHUB_ACTION = 'CODEX_GITHUB_ACTION',
  CODEX_SDK_TS = 'CODEX_SDK_TS',
  CODEX_SERVICE_EXEC = 'CODEX_SERVICE_EXEC',
  CODEX_DESKTOP_APP = 'CODEX_DESKTOP_APP',
  CODEX_UNKNOWN_DEFAULT = 'CODEX_UNKNOWN_DEFAULT',
}

export enum InlineClientApplication {
  copilotStudio = 'copilotStudio',
  chatGpt = 'chatGpt',
  gitHubCopilotVisualStudio = 'gitHubCopilotVisualStudio',
  claudeDesktop = 'claudeDesktop',
  copilot = 'copilot',
  perplexity = 'perplexity',
  gitHubCopilotWeb = 'gitHubCopilotWeb',
  cursor = 'cursor',
  chatKit = 'chatKit',
  claudeCode = 'claudeCode',
  slack = 'slack',
}

// apps that have icons
export enum AgentClientApplicationIcon {
  office = 'office',
  teams = 'teams',
  word = 'word',
  excel = 'excel',
  powerpoint = 'powerpoint',
  outlook = 'outlook',
  onenote = 'onenote',
  sharepoint = 'sharepoint',
  stream = 'stream',
  forms = 'forms',
  bing = 'bing',
  agentWebsite = 'agentWebsite',
  testing = 'testing',
  autonomous = 'autonomous',
  chatGpt = 'chatGpt',
  azureAiFoundry = 'azureAiFoundry',
  vertexAi = 'vertexAi',
  serviceNow = 'serviceNow',
}

export interface ClientContext {
  clientApplication: AgentClientApplication;
  region?: string;
  ipAddress?: string;
  chatMode?: string;
}

export enum LlmProvider {
  openAi = 'openAi',
  anthropic = 'anthropic',
  custom = 'custom',
  google = 'google',
}

export interface AIToolPlatformInfo {
  resourceId: string;
}

export interface BotPlatformInfo {
  resourceId: string;
}

export interface AIToolDefinition {
  platformInfo: AIToolPlatformInfo;
  inventoryId?: string;
  displayName?: string;
  description?: string;
  paramsDefinition?: Record<string, unknown>;
}

export enum LLMModelFamily {
  gptO1Series = 'gptO1Series',
  gpt4Series = 'gpt4Series',
  claude3Family = 'claude3Family',
  claude35Family = 'claude35Family',
  gemini = 'gemini',
}

export interface BotDetails {
  platformInfo: BotPlatformInfo;
  inventoryId?: string;
  displayName: string;
  llmProvider: LlmProvider;
  modelFamily?: LLMModelFamily;
  modelName: string;
  availableTools?: AIToolDefinition[];
}

export interface ToolInvocations {
  toolDisplayName?: string;
  toolOrigin?: string;
  toolPlatformInfo?: AIToolPlatformInfo;
  toolId?: string;
  executionId?: string;
}

export enum SourceType {
  m365CopilotSource = 'm365CopilotSource',
  chatGptEnterpriseSource = 'chatGptEnterpriseSource',
  vertexAiSource = 'vertexAiSource',
}

export interface SourceEntry {
  id: string;
  type: SourceType;
  name?: string;
  url?: string;
}

/**
 * Sensitivity label containing both ID and human-readable name.
 */
export interface SensitivityLabel {
  /** The GUID of the sensitivity label from Microsoft. */
  id?: string;
  /** The human-readable name of the sensitivity label (with parent hierarchy). */
  name?: string;
}

export interface M365CopilotSource extends SourceEntry {
  /** @deprecated Use sensitivityLabel instead */
  sensitivityLabelId?: string;
  /** The sensitivity label object containing both ID and name. */
  sensitivityLabel?: SensitivityLabel;
  m365ResourceType: string;
}

export interface ChatGptEnterpriseSource extends SourceEntry {
  chatGptEnterpriseResourceType: string;
}

export interface AzureAiFoundrySource extends SourceEntry {
  azureAiFoundryResourceType: string;
}

export interface VertexAiSource extends SourceEntry {
  vertexAiSourceType: string;
  contentAvailable?: boolean;
}

export interface ServiceNowSource extends SourceEntry {
  serviceNowSourceType: string;
  tableName?: string;
  recordSysId?: string;
  contentAvailable?: boolean;
  displayValue?: string;
}

export interface AssociatedIntegration {
  id: string;
}

export interface AssociatedThreadId {
  id: string;
}

export interface AttachedDocument {
  id: string;
  displayName: string;
}

export interface Environment {
  id: string;
  displayName: string;
}

export class BaseAgentStepModel {
  public id: string;
  public relatedActors: Actor[];
  public mainActor: Actor;
  public timestamp: Date;
  public type: StepType;
  public thread: AssociatedThreadId;
  public service: AiService;
  public indicators: Indicator[];
  public stepTriggerIds: string[];
  public botDetails: BotDetails;
  public clientContext: ClientContext;
  public zenityAccountId: string;
  public integration: AssociatedIntegration;
  public triggeringStepsIds?: string[];
  public environment?: Environment;
  public source?: ActivitySource;
  public relatedFindingsIds?: string[];
}

export class UserMessageAgentStepModel extends BaseAgentStepModel {
  public attachedDocuments?: AttachedDocument[];
  public isTranscriptFetchingAllowed?: boolean;
  public override type: StepType.userMessageStep;
}

export class AIMessageStep extends BaseAgentStepModel {
  public documentOutput?: AttachedDocument[];
  public isTranscriptFetchingAllowed?: boolean;
  public override type: StepType.aiMessageStep;
}

export class RagStep<TSourceEntry extends SourceEntry = SourceEntry> extends BaseAgentStepModel {
  public sources?: TSourceEntry[];
  public citedSources: TSourceEntry[];
  public ragQuery?: string;
  public ragQueryAvailable?: boolean;
  public override type: StepType.ragStep;
}

export class M365CopilotRagStep extends RagStep<M365CopilotSource> {
  /** @deprecated Use sensitivityLabel instead */
  public sensitivityLabelId?: string;
  /** The sensitivity label object containing both ID and name. */
  public sensitivityLabel?: SensitivityLabel;
  public override service: AiService.m365Copilot;
}

export class CopilotStudioRagStep extends RagStep<M365CopilotSource> {
  /** @deprecated Use sensitivityLabel instead */
  public sensitivityLabelId?: string;
  /** The sensitivity label object containing both ID and name. */
  public sensitivityLabel?: SensitivityLabel;
  public override service: AiService.copilotStudio;
}

export class M365AIMessageStep extends AIMessageStep {
  public override service: AiService.m365Copilot;
  /** @deprecated Use sensitivityLabel instead */
  public sensitivityLabelId?: string;
  /** The sensitivity label object containing both ID and name. */
  public sensitivityLabel?: SensitivityLabel;
}

export class CopilotStudioAIMessageStep extends AIMessageStep {
  public override service: AiService.copilotStudio;
}

export class PerplexityAIMessageStep extends AIMessageStep {
  public override service: AiService.perplexity;
}

export class AgentKitAIMessageStep extends AIMessageStep {
  public override service: AiService.agentKit;
}

export class ChatGptEnterpriseAIMessageStep extends AIMessageStep {
  public override service: AiService.chatGptEnterprise;
}

export class CodexAIMessageStep extends AIMessageStep {
  public override service: AiService.openaiCodex;
}

export class AzureAiFoundryAIMessageStep extends AIMessageStep {
  public override service: AiService.azureAiFoundry;
}

export class M365CopilotUserMessageStep extends UserMessageAgentStepModel {
  public override service: AiService.m365Copilot;
  public override attachedDocuments?: AttachedDocument[];
}

export class PerplexityUserMessageStep extends UserMessageAgentStepModel {
  public override service: AiService.perplexity;
  public override attachedDocuments?: AttachedDocument[];
}

export class AgentKitUserMessageStep extends UserMessageAgentStepModel {
  public override service: AiService.agentKit;
  public override attachedDocuments?: AttachedDocument[];
}
export class CopilotStudioUserMessageStep extends UserMessageAgentStepModel {
  public override service: AiService.copilotStudio;
  public override attachedDocuments?: AttachedDocument[];
}

export class ChatGptEnterpriseUserMessageStep extends UserMessageAgentStepModel {
  public override service: AiService.chatGptEnterprise;
  public override attachedDocuments?: AttachedDocument[];
}

export class CodexUserMessageStep extends UserMessageAgentStepModel {
  public override service: AiService.openaiCodex;
}

export class AzureAiFoundryUserMessageStep extends UserMessageAgentStepModel {
  public override service: AiService.azureAiFoundry;
  public override attachedDocuments?: AttachedDocument[];
}

export class VertexAiAIMessageStep extends AIMessageStep {
  public override service: AiService.vertexAi;
}

export class VertexAiUserMessageStep extends UserMessageAgentStepModel {
  public override service: AiService.vertexAi;
  public override attachedDocuments?: AttachedDocument[];
}

export class ServiceNowUserMessageStep extends UserMessageAgentStepModel {
  public override service: AiService.serviceNow;
  public override attachedDocuments?: AttachedDocument[];
}

export class ServiceNowAIMessageStep extends AIMessageStep {
  public override service: AiService.serviceNow;
}

export class TriggerStep extends BaseAgentStepModel {
  public override type: StepType.triggerStep;
}

export class ToolInvocationStep extends BaseAgentStepModel {
  public override type: StepType.toolInvocationStep;
  public toolInvocations: ToolInvocations[];
}

// Tool request/response classes - two variants exist for backward compatibility:
// - ToolInvocationRequestStep/ToolInvocationResultStep: Original types for inline activity pipeline
// - ToolRequestStep/ToolResponseStep: New types for unified pipe (audit events pipeline)
// Both pairs have identical UI behavior but different API values and property names

// Original tool request step type (inline activity pipeline)
export class ToolInvocationRequestStep extends BaseAgentStepModel {
  public override type: StepType.toolInvocationRequestStep;
  public toolInvocations: ToolInvocations[];
  public messageId?: string;
}

// Original tool result step type (inline activity pipeline)
export class ToolInvocationResultStep extends BaseAgentStepModel {
  public override type: StepType.toolInvocationResultStep;
  public toolInvocations: ToolInvocations[];
  public executionId?: string;
  public messageId?: string;
}

// New tool request step type (unified pipe / audit events pipeline)
export class ToolRequestStep extends BaseAgentStepModel {
  public override type: StepType.toolRequestStep;
  public toolInvocationRequests: ToolInvocations[];
  public messageId?: string;
}

// New tool response step type (unified pipe / audit events pipeline)
export class ToolResponseStep extends BaseAgentStepModel {
  public override type: StepType.toolResponseStep;
  public toolInvocationResponses: ToolInvocations[];
  public executionId?: string;
  public messageId?: string;
}

export class CopilotStudioTriggerStep extends TriggerStep {
  public triggerDisplayName?: string;
  public triggerIconUrl?: string;
  public override service: AiService.copilotStudio;
}

export class CopilotStudioToolInvocationStep extends ToolInvocationStep {
  public triggerDisplayName?: string;
  public triggerIconUrl?: string;
  public override service: AiService.copilotStudio;
}

export class CopilotStudioToolInvocationRequestStep extends ToolInvocationRequestStep {
  public triggerDisplayName?: string;
  public triggerIconUrl?: string;
  public override service: AiService.copilotStudio;
}

export class ChatGptEnterpriseRagStep extends RagStep<ChatGptEnterpriseSource> {
  public override service: AiService.chatGptEnterprise;
}

export class AzureAiFoundryRagStep extends RagStep<AzureAiFoundrySource> {
  public override service: AiService.azureAiFoundry;
}

export class VertexAiRagStep extends RagStep<VertexAiSource> {
  public override service: AiService.vertexAi;
}

export class AgentforceRagStep extends RagStep {
  public override service: AiService.agentforce;
}

export class ChatGptEnterpriseToolInvocationStep extends ToolInvocationStep {
  public override service: AiService.chatGptEnterprise;
  public invocationsDetails?: {
    server?: string;
    path?: string;
    method?: string;
    operationSchema?: string;
  }[];
}

export class ChatGptEnterpriseToolInvocationRequestStep extends ToolInvocationRequestStep {
  public override service: AiService.chatGptEnterprise;
  public invocationsDetails?: {
    server?: string;
    path?: string;
    method?: string;
    operationSchema?: string;
  }[];
}

export class AzureAiFoundryToolInvocationStep extends ToolInvocationStep {
  public override service: AiService.azureAiFoundry;
}

export class VertexAiToolInvocationStep extends ToolInvocationStep {
  public override service: AiService.vertexAi;
}

export class ServiceNowToolInvocationStep extends ToolInvocationStep {
  public override service: AiService.serviceNow;
  public executionPlanId?: string;
  public runAsUser?: string;
}

export class AzureAiFoundryToolInvocationRequestStep extends ToolInvocationRequestStep {
  public override service: AiService.azureAiFoundry;
}

export class VertexAiToolInvocationRequestStep extends ToolInvocationRequestStep {
  public override service: AiService.vertexAi;
}

export class ServiceNowToolInvocationRequestStep extends ToolInvocationRequestStep {
  public override service: AiService.serviceNow;
  public executionPlanId?: string;
  public runAsUser?: string;
}

export class BedrockAgentCoreToolInvocationRequestStep extends ToolInvocationRequestStep {
  public override service: AiService.bedrockAgentCore;
}

export class CopilotStudioToolInvocationResultStep extends ToolInvocationResultStep {
  public triggerDisplayName?: string;
  public triggerIconUrl?: string;
  public override service: AiService.copilotStudio;
}

export class ChatGptEnterpriseToolInvocationResultStep extends ToolInvocationResultStep {
  public override service: AiService.chatGptEnterprise;
  public invocationsDetails?: {
    server?: string;
    path?: string;
    method?: string;
    operationSchema?: string;
  }[];
}

export class AzureAiFoundryToolInvocationResultStep extends ToolInvocationResultStep {
  public override service: AiService.azureAiFoundry;
}

export class VertexAiToolInvocationResultStep extends ToolInvocationResultStep {
  public override service: AiService.vertexAi;
}

export class ServiceNowToolInvocationResultStep extends ToolInvocationResultStep {
  public override service: AiService.serviceNow;
  public executionPlanId?: string;
  public runAsUser?: string;
}

export class BedrockAgentCoreToolInvocationResultStep extends ToolInvocationResultStep {
  public override service: AiService.bedrockAgentCore;
}

export class AgentForceToolInvocationRequestStep extends ToolInvocationRequestStep {
  public override service: AiService.agentforce;
}

export class AgentForceToolInvocationResultStep extends ToolInvocationResultStep {
  public override service: AiService.agentforce;
}

// Platform-specific ToolRequestStep variants
export class CopilotStudioToolRequestStep extends ToolRequestStep {
  public triggerDisplayName?: string;
  public triggerIconUrl?: string;
  public override service: AiService.copilotStudio;
}

export class ChatGptEnterpriseToolRequestStep extends ToolRequestStep {
  public override service: AiService.chatGptEnterprise;
  public invocationsDetails?: {
    server?: string;
    path?: string;
    method?: string;
    operationSchema?: string;
  }[];
}

export class AzureAiFoundryToolRequestStep extends ToolRequestStep {
  public override service: AiService.azureAiFoundry;
}

export class VertexAiToolRequestStep extends ToolRequestStep {
  public override service: AiService.vertexAi;
}

export class ServiceNowToolRequestStep extends ToolRequestStep {
  public override service: AiService.serviceNow;
  public executionPlanId?: string;
  public runAsUser?: string;
}

export class BedrockAgentCoreToolRequestStep extends ToolRequestStep {
  public override service: AiService.bedrockAgentCore;
}

export class AgentForceToolRequestStep extends ToolRequestStep {
  public override service: AiService.agentforce;
}

// Platform-specific ToolResponseStep variants
export class CopilotStudioToolResponseStep extends ToolResponseStep {
  public triggerDisplayName?: string;
  public triggerIconUrl?: string;
  public override service: AiService.copilotStudio;
}

export class ChatGptEnterpriseToolResponseStep extends ToolResponseStep {
  public override service: AiService.chatGptEnterprise;
  public invocationsDetails?: {
    server?: string;
    path?: string;
    method?: string;
    operationSchema?: string;
  }[];
}

export class AzureAiFoundryToolResponseStep extends ToolResponseStep {
  public override service: AiService.azureAiFoundry;
}

export class VertexAiToolResponseStep extends ToolResponseStep {
  public override service: AiService.vertexAi;
}

export class ServiceNowToolResponseStep extends ToolResponseStep {
  public override service: AiService.serviceNow;
  public executionPlanId?: string;
  public runAsUser?: string;
}

export class BedrockAgentCoreToolResponseStep extends ToolResponseStep {
  public override service: AiService.bedrockAgentCore;
}

export class AgentForceToolResponseStep extends ToolResponseStep {
  public override service: AiService.agentforce;
}

export class ChatGptEnterpriseMemoryStep extends BaseAgentStepModel {
  public override service: AiService.chatGptEnterprise;
  public override type: StepType.memoryStep;
  public isTranscriptFetchingAllowed?: boolean;
}

export class BedrockAgentCoreToolInvocationStep extends ToolInvocationStep {
  public override service: AiService.bedrockAgentCore;
}

export class BedrockAgentCoreAIMessageStep extends AIMessageStep {
  public override service: AiService.bedrockAgentCore;
}

export class BedrockAgentCoreUserMessageStep extends UserMessageAgentStepModel {
  public override service: AiService.bedrockAgentCore;
}

export class AgentHandoffRequestStep extends BaseAgentStepModel {
  public override type: StepType.agentHandoffRequestStep;
  public siblingStepId?: string;
  public requestingAgent?: BotDetails;
  public respondingAgent?: BotDetails;
  public isTranscriptFetchingAllowed?: boolean;
}

export class AgentHandoffResponseStep extends BaseAgentStepModel {
  public override type: StepType.agentHandoffResponseStep;
  public siblingStepId?: string;
  public requestingAgent?: BotDetails;
  public respondingAgent?: BotDetails;
  public isTranscriptFetchingAllowed?: boolean;
}

export class CopilotStudioAgentHandoffRequestStep extends AgentHandoffRequestStep {
  public override service: AiService.copilotStudio;
}

export class CopilotStudioAgentHandoffResponseStep extends AgentHandoffResponseStep {
  public override service: AiService.copilotStudio;
}

export type AgentStepModel =
  | CopilotStudioRagStep
  | M365CopilotRagStep
  | ChatGptEnterpriseRagStep
  | M365AIMessageStep
  | M365CopilotUserMessageStep
  | CopilotStudioTriggerStep
  | CopilotStudioToolInvocationStep
  | ChatGptEnterpriseToolInvocationStep
  | AzureAiFoundryRagStep
  | AzureAiFoundryToolInvocationStep
  | AzureAiFoundryAIMessageStep
  | AzureAiFoundryUserMessageStep
  | VertexAiRagStep
  | AgentforceRagStep
  | VertexAiToolInvocationStep
  | VertexAiAIMessageStep
  | VertexAiUserMessageStep
  | ServiceNowToolInvocationStep
  | ServiceNowAIMessageStep
  | ServiceNowUserMessageStep
  | CopilotStudioAIMessageStep
  | CopilotStudioUserMessageStep
  | PerplexityAIMessageStep
  | PerplexityUserMessageStep
  | AgentKitAIMessageStep
  | AgentKitUserMessageStep
  | ChatGptEnterpriseAIMessageStep
  | ChatGptEnterpriseUserMessageStep
  | ChatGptEnterpriseMemoryStep
  | CodexAIMessageStep
  | CodexUserMessageStep
  | BedrockAgentCoreToolInvocationStep
  | BedrockAgentCoreAIMessageStep
  | BedrockAgentCoreUserMessageStep
  | ToolInvocationRequestStep
  | ToolInvocationResultStep
  | CopilotStudioToolInvocationRequestStep
  | ChatGptEnterpriseToolInvocationRequestStep
  | AzureAiFoundryToolInvocationRequestStep
  | VertexAiToolInvocationRequestStep
  | ServiceNowToolInvocationRequestStep
  | BedrockAgentCoreToolInvocationRequestStep
  | CopilotStudioToolInvocationResultStep
  | ChatGptEnterpriseToolInvocationResultStep
  | AzureAiFoundryToolInvocationResultStep
  | VertexAiToolInvocationResultStep
  | ServiceNowToolInvocationResultStep
  | BedrockAgentCoreToolInvocationResultStep
  | AgentForceToolInvocationRequestStep
  | AgentForceToolInvocationResultStep
  | ToolRequestStep
  | ToolResponseStep
  | CopilotStudioToolRequestStep
  | ChatGptEnterpriseToolRequestStep
  | AzureAiFoundryToolRequestStep
  | VertexAiToolRequestStep
  | ServiceNowToolRequestStep
  | BedrockAgentCoreToolRequestStep
  | AgentForceToolRequestStep
  | CopilotStudioToolResponseStep
  | ChatGptEnterpriseToolResponseStep
  | AzureAiFoundryToolResponseStep
  | VertexAiToolResponseStep
  | ServiceNowToolResponseStep
  | BedrockAgentCoreToolResponseStep
  | AgentForceToolResponseStep
  | AgentHandoffRequestStep
  | AgentHandoffResponseStep
  | CopilotStudioAgentHandoffRequestStep
  | CopilotStudioAgentHandoffResponseStep;
